/*
 * Assisted Conversion / Influenced Revenue: on-demand loading for the Ad tab, with NO cache.
 *
 * Both columns come from one bulk endpoint (get_aggregated_ad_assist_metrics_bulk) that answers
 * BOTH metrics for every requested (ad, goal), so one request per need covers every visible cell
 * of the goals asked about - never one request per cell.
 *
 * Assisted Conversion is what asks the backend; Influenced Revenue only shows what that answer
 * already carried:
 *
 *   - Checking Assisted Conversion on a goal always fetches it again, even when the goal is
 *     already on screen because its Influenced Revenue column is showing.
 *   - The Influenced Revenue checkbox never sends a request of its own. Switched on beside a
 *     loaded Assisted Conversion column it draws from the same answer; switched on alone it
 *     shows what the goal already has, and no figure when the goal has none.
 *   - Hiding either column never sends a request.
 *   - A goal's figures are released when neither of its columns is on screen, so Influenced
 *     Revenue keeps showing them while Assisted Conversion is hidden.
 *   - Every new table response (page, sort, search, Save / re-apply, another report) releases
 *     everything and fetches the goals whose Assisted Conversion column is on.
 *
 * Failures:
 *
 *   A failed request, or a goal the endpoint reports in failed_goal_keys, leaves its cells
 *   "failed" - shown as such, never as 0 or $0.00 - and Retry asks for all of them again in one
 *   request. A genuine $0.00 is an answer and is shown as one.
 *
 * Which answer may be shown:
 *
 *   Requests are made with the settings the table's rows were ACTUALLY loaded with (an
 *   AssistContext snapshot taken when the Ad table's response landed), never with the live header
 *   or drawer state. Each table response opens a new generation; an answer is accepted only by
 *   the generation that asked for it, and only for cells still visible when it lands. An answer
 *   that arrives after the report was re-applied, after the table moved on, or after its columns
 *   were hidden is discarded.
 */
import { useEffect, useRef, useState } from 'react';
import { assistCellKey, AssistValues } from './assists';

export type AssistContext = {
  report_id: string;
  visiopt_campaign_id: string[];
  attribution_model: string;
  report_time: string;
  attribute_window_type: string;
  lookback_days: number;
  /* The report's apply counter when these rows were loaded. */
  applied: number;
};

export type AssistRow = { id: string; source: string };

export type AssistRequest = {
  goal_keys: string[];
  ad_entity_ids: string[];
  sources: string[];
};

/* What the bulk endpoint returns: goal key -> ad id -> figures. */
export type AssistCells = Record<string, Record<string, Partial<AssistValues> | undefined> | undefined>;

/* One successful bulk response. Goals in failedGoalKeys could not be computed this time and
   carry no figures. A request that failed outright rejects instead. */
export type AssistResult = { cells: AssistCells; failedGoalKeys: string[] };

export type AssistFetcher = (params: Record<string, unknown>) => Promise<AssistResult>;

/*
 * The bulk endpoint's JSON body, reduced to what the loader shows.
 *
 * Anything but status 1 throws, so nothing from a failed request can be shown as a figure - the
 * endpoint answers status 0 when a request is invalid or none of its queries ran. A successful
 * body can still name goals it could not compute in failed_goal_keys; those come back as
 * failures. PHP encodes an empty map as a JSON array ("cells": []), read as no cells.
 */
export const parseAssistResponse = (body: any): AssistResult => {
  if (!body || body.status !== 1) {
    throw new Error((body && body.message) || 'Assisted Conversion request failed');
  }
  const raw = body.data ? body.data.cells : undefined;
  const cells: AssistCells = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const failed = body.data ? body.data.failed_goal_keys : undefined;
  return { cells, failedGoalKeys: Array.isArray(failed) ? failed.map(String) : [] };
};

/* 'idle': nothing is coming - the goal shows only Influenced Revenue and has no figures, so the
   cell reads as not measured rather than waiting forever on a request nobody will send. */
export type AssistCellState = 'loaded' | 'loading' | 'failed' | 'idle';

/*
 * The one request that covers everything still needed, or null when nothing is.
 *
 * The request names every goal that needs something and every ad any of those goals needs. The
 * endpoint's cost is one query per (source x goal) whatever the ad list, so answering a goal for
 * a few extra ads is free, while a second request would not be.
 */
export const planAssistRequest = (
  goalKeys: string[],
  rows: AssistRow[],
  isKnown: (cellKey: string) => boolean
): AssistRequest | null => {
  const goals = new Set<string>();
  const ids = new Map<string, string>();          // ad id -> source
  for (const goalKey of goalKeys) {
    for (const r of rows) {
      if (!isKnown(assistCellKey(r.id, goalKey))) {
        goals.add(goalKey);
        ids.set(r.id, r.source);
      }
    }
  }
  if (goals.size === 0) { return null; }
  return {
    goal_keys: Array.from(goals),
    ad_entity_ids: Array.from(ids.keys()),
    sources: Array.from(new Set(ids.values())),
  };
};

/* The figures for the cells on screen. Not a cache: it is emptied by every new table response,
   by hiding both of a goal's columns, and by checking Assisted Conversion again. */
type Screen = {
  gen: number;
  ctx: string;
  rows: AssistRow[];
  /* Answered cells. null: answered, but the goal has no measurable figure for the ad. */
  cells: Record<string, AssistValues | null>;
  failed: Record<string, true>;
  /* Cells on their way, by the id of the request carrying them. */
  inflight: Record<string, number>;
};
const NO_ROWS: AssistRow[] = [];
const emptyScreen = (gen: number, ctx: string, rows: AssistRow[]): Screen =>
  ({ gen, ctx, rows, cells: {}, failed: {}, inflight: {} });
const has = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

export const useAssistLoader = (opts: {
  /* False when the figures cannot or need not be fetched: attribution that measures no
     assists, figures already inline in the payload, or not the Ad tab. */
  enabled: boolean;
  context: AssistContext | null | undefined;
  level: string;
  /* Goals whose ASSISTED CONVERSION column is visible. These are what gets fetched: a goal
     entering this list has its figures released and asked for again. */
  fetchGoalKeys: string[];
  /* Goals with EITHER assist column visible. These keep their figures and render them, so
     Influenced Revenue can show what Assisted Conversion fetched. */
  visibleGoalKeys: string[];
  /* The rows on screen, as the table last delivered them. A new array is a new table response. */
  rows: AssistRow[];
  fetcher: AssistFetcher;
}) => {
  const { enabled, context, level, fetchGoalKeys, visibleGoalKeys, rows } = opts;
  const ctxKey = enabled && context ? JSON.stringify({ ...context, level }) : '';

  const [screen, setScreen] = useState<Screen>(() => emptyScreen(0, '', NO_ROWS));
  const [retryTick, setRetryTick] = useState(0);
  /* Read inside async callbacks, which must see the latest state, not the one their render saw. */
  const screenRef = useRef<Screen>(screen);
  const visibleRef = useRef<Set<string>>(new Set());
  /* Which goals were being fetched on the previous run, to spot a column just switched on. */
  const fetchedGoalsRef = useRef<Set<string>>(new Set());
  const retrySeenRef = useRef(0);
  const requestSeqRef = useRef(0);
  const fetcherRef = useRef<AssistFetcher>(opts.fetcher);
  fetcherRef.current = opts.fetcher;

  const commit = (next: Screen) => {
    screenRef.current = next;
    setScreen(next);
  };

  const fetchKey = JSON.stringify([...fetchGoalKeys].sort());
  const visibleKey = JSON.stringify([...visibleGoalKeys].sort());

  useEffect(() => {
    if (ctxKey === '' || !context) { return; }

    /* A new configuration or a new table response: nothing held describes these rows any more,
       and any answer still in flight belongs to the previous generation. (An empty list stays
       the same list, so a table with no rows cannot churn generations on every render.) */
    const prev = screenRef.current;
    const rowsChanged = prev.rows !== rows && !(rows.length === 0 && prev.rows.length === 0);
    if (prev.ctx !== ctxKey || rowsChanged) {
      commit(emptyScreen(prev.gen + 1, ctxKey, rows));
      fetchedGoalsRef.current = new Set();
    }

    const retrying = retrySeenRef.current !== retryTick;
    retrySeenRef.current = retryTick;

    const visible = new Set<string>();
    for (const goalKey of visibleGoalKeys) {
      for (const r of rows) { visible.add(assistCellKey(r.id, goalKey)); }
    }
    visibleRef.current = visible;

    let cur = screenRef.current;

    /* Cells of a goal with neither column on screen keep nothing; showing one again asks the
       backend again. What is in flight is left alone - its answer is dropped on arrival if the
       cell is still hidden then. */
    const hidden = Object.keys(cur.cells).concat(Object.keys(cur.failed)).filter((k) => !visible.has(k));
    /* Assisted Conversion just switched on for a goal: its figures are refreshed, even when the
       goal was already on screen because Influenced Revenue was showing them. */
    const justAsked: string[] = [];
    for (const goalKey of fetchGoalKeys) {
      if (fetchedGoalsRef.current.has(goalKey)) { continue; }
      for (const r of rows) { justAsked.push(assistCellKey(r.id, goalKey)); }
    }
    fetchedGoalsRef.current = new Set(fetchGoalKeys);

    const release = hidden.concat(justAsked);
    if (release.length > 0) {
      const cells = { ...cur.cells };
      const failed = { ...cur.failed };
      release.forEach((k) => { delete cells[k]; delete failed[k]; });
      commit({ ...cur, cells, failed });
      cur = screenRef.current;
    }

    const needsFetch = (k: string): boolean => {
      if (has(cur.cells, k)) { return false; }                 // on screen from this generation
      if (has(cur.inflight, k)) { return false; }              // already on its way
      return !cur.failed[k] || retrying;                       // failed: only on Retry
    };

    const plan = planAssistRequest(fetchGoalKeys, rows, (k) => !needsFetch(k));
    if (!plan) { return; }

    const needed: Array<{ k: string; goalKey: string; id: string }> = [];
    for (const goalKey of plan.goal_keys) {
      for (const id of plan.ad_entity_ids) {
        const k = assistCellKey(id, goalKey);
        if (needsFetch(k)) { needed.push({ k, goalKey, id }); }
      }
    }

    const requestId = ++requestSeqRef.current;
    const gen = cur.gen;
    const inflight = { ...cur.inflight };
    needed.forEach(({ k }) => { inflight[k] = requestId; });
    commit({ ...cur, inflight });

    const settle = (result: AssistResult | null) => {
      const s = screenRef.current;
      if (s.gen !== gen) { return; }                           // a newer generation owns the screen
      const next: Screen = {
        ...s,
        cells: { ...s.cells },
        failed: { ...s.failed },
        inflight: { ...s.inflight },
      };
      const failedGoals = new Set(result ? result.failedGoalKeys : []);
      for (const { k, goalKey, id } of needed) {
        if (next.inflight[k] !== requestId) { continue; }
        delete next.inflight[k];
        if (!visibleRef.current.has(k)) { continue; }          // hidden meanwhile: keep nothing
        if (!result || failedGoals.has(goalKey)) {
          next.failed[k] = true;
          continue;
        }
        const v = result.cells[goalKey]?.[id];
        next.cells[k] = v
          ? { assisted: Number(v.assisted ?? 0), influenced_revenue: v.influenced_revenue ?? null }
          : null;
        delete next.failed[k];
      }
      commit(next);
    };

    fetcherRef.current({
      report_id: context.report_id,
      visiopt_campaign_id: context.visiopt_campaign_id,
      attribution_model: context.attribution_model,
      report_time: context.report_time,
      attribute_window_type: context.attribute_window_type,
      lookback_days: context.lookback_days,
      level,
      goal_keys: plan.goal_keys,
      sources: plan.sources,
      ad_entity_ids: plan.ad_entity_ids,
    }).then(
      (result) => settle(result ?? null),
      () => settle(null)
    );
    // The goal lists and context are read through fetchKey / visibleKey / ctxKey, which change
    // exactly when their content does. `rows` is compared by identity: a new array is a new
    // table response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxKey, fetchKey, visibleKey, rows, retryTick]);

  /* Until the effect has opened the generation for these rows, nothing on screen is theirs. */
  const current = ctxKey !== '' && screen.ctx === ctxKey && screen.rows === rows;
  const fetching = new Set(fetchGoalKeys);

  const cellState = (adId: string, goalKey: string): AssistCellState => {
    const waiting: AssistCellState = fetching.has(goalKey) ? 'loading' : 'idle';
    if (!current) { return waiting; }
    const k = assistCellKey(adId, goalKey);
    if (has(screen.inflight, k)) { return 'loading'; }
    if (screen.failed[k]) { return 'failed'; }
    if (has(screen.cells, k)) { return 'loaded'; }
    return waiting;
  };

  return {
    cellState,
    /* Figures for one cell once loaded - the same answer behind both columns. Undefined while
       loading, after a failure, and when the goal has no figure for the ad. */
    value: (adId: string, goalKey: string): AssistValues | undefined =>
      cellState(adId, goalKey) === 'loaded' ? (screen.cells[assistCellKey(adId, goalKey)] ?? undefined) : undefined,
    /* Ask again for every failed cell on screen - one request, never one per cell. */
    retry: () => setRetryTick((t) => t + 1),
  };
};
